import { usePaymentStore } from "@/components/payment/usePaymentStore";
import type { TimeSlot } from "@/components/types";
import { useBooking as useBookingStore } from "@/store/useBooking";
import { useGlobalErrorStore } from "@/store/useGlobalError";
import { useTodPricing } from "@/store/useTodPricing";
import { getBookingStatus } from "@/utils/Formatting";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";
import isEmpty from "lodash/isEmpty";
import { useUserStore } from "@/utils/UserStore";
import { getBookingOptionQuantities, getBookingOptionQuantity } from "@/utils/bookingOptionQuantities";

// Only the first record (sorted by sortOrder) that matches the day and
// falls within its validFrom/validTill range is used
export const getAvailableHoursForDay = (timeRanges: any, day: any, date?: any) => {
  if (!timeRanges) return undefined;
  const dateCheck = date ? moment(date) : null;
  const matchedTimeRange = timeRanges
    .filter((el: any) => {
      if (el[day] !== true) return false;
      if (dateCheck) {
        // Compare by calendar day only — validFrom/validTill are stored as the
        // start of their day, but callers like canPayInStore() pass a full
        // date+time, which would otherwise look "after" a same-day validTill.
        if (el?.validFrom && el.validFrom !== '' && dateCheck.isBefore(moment(el.validFrom), 'day')) return false;
        if (el?.validTill && el.validTill !== '' && dateCheck.isAfter(moment(el.validTill), 'day')) return false;
      }
      return true;
    })
    .slice()
    .sort((a: any, b: any) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0))[0];

  if (!matchedTimeRange) return [];

  const { startHour, finishHour } = matchedTimeRange;
  if (finishHour === 0) {
    return [
      ...Array.from({ length: 24 - startHour }, (_, i) => startHour + i), // From startHour to 23
      ...Array.from({ length: finishHour + 1 }, (_, i) => i), // From 0 to finishHour
    ];
  }

  return Array.from(
    { length: finishHour - startHour + 1 },
    (_, i) => startHour + i,
  );
};

export const useBooking = () => {
  // composable
  const bookingStore = useBookingStore();
  const paymentStore = usePaymentStore();
  const globalStore = useGlobalSetupStore();
  const globalError = useGlobalErrorStore();
  const todStore = useTodPricing();
  const { userId, currentLocation } = storeToRefs(useUserStore());

  const { editBookingData, recurringBookingId } = storeToRefs(bookingStore);
  const { docketId, docketLineId, isPendingBooking } = storeToRefs(paymentStore);
  const { globalSetup } = storeToRefs(globalStore);
  const { getItemTOD } = todStore;
  const { isBookingWarningDialogVisible, errorDateList } = storeToRefs(globalError);

  // Data
  const paymentOptions = ref<any[]>([
    {
      title: "Coupon / Gift voucher",
      value: "couponOrGift",
    },
    {
      title: "User Credit",
      value: "userCredit",
    },
    {
      title: "POS",
      value: "pos",
    },
    {
      title: "Waive Fee",
      value: "waiveFee",
    },
  ]);

  const recurringWeeks = ref([
    { id: 1, label: "M", value: "Monday" },
    { id: 2, label: "T", value: "Tuesday" },
    { id: 3, label: "W", value: "Wednesday" },
    { id: 4, label: "T", value: "Thursday" },
    { id: 5, label: "F", value: "Friday" },
    { id: 6, label: "S", value: "Saturday" },
    { id: 7, label: "S", value: "Sunday" },
  ]);

  const alternativeAreaList = ref<any[]>([]);

  // computed
  const getSelectedRecurringItem = computed(() => (type: string) => {
    // Map recurringWeeks by id
    return recurringWeeks.value.reduce((acc: any, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  });

  const recurringEndDateConfig = computed(() => (maxWeek: number) => {
    // Calculate the maximum date using Moment.js
    const maxDate = moment().add(maxWeek * 7, "days");
    return {
      dateFormat: "d-m-Y",
      disable: [
        function (date: any) {
          const today = moment().startOf("day");
          const momentDate = moment(date).startOf("day");
          // Disable past dates or dates beyond the max week range
          return momentDate.isBefore(today) || momentDate.isAfter(maxDate);
        },
      ],
    };
  });

  const getAreaSingular = computed(() => {
    return globalSetup?.value?.areaSingular || "Bay";
  });

  const getColor = (item: TimeSlot) => {
    if (item?.isBooked) {
      const { due, state, status: bookingStatus } = item?.bookingDetails || {};
      // Determine color based on booking status and payment
      switch (true) {
        case state === "Pending":
          // "#F7DC6F" - Yellow Case 1: Booking state is pending
          return "yellow_bg";
        case !due && bookingStatus === "futureBooking":
          // "#E5E7E9" - Grey Case 2: Paid, future booking
          return "gray_bg";
        case !due && bookingStatus === "inprogress":
          // "#82E0AA" - Green Case 3: Paid, booking in progress
          return "green_bg";
        case due && bookingStatus === "futureBooking":
          // "#F8C471" - Orange Case 4: Not paid, future booking
          return "orange_bg";
        case due && bookingStatus === "inprogress":
          // "#F1948A" - Red Case 5: Not paid, booking in progress
          return "red_bg";
        case !due && bookingStatus === "completed":
          // "#99A3A4" - DarkGrey Case 6: Paid, booking completed
          return "darkgray_bg";
        case due && bookingStatus === "completed":
          // "#F1948A" - Red Case 7: Not paid, booking completed
          return "red_bg";
        default:
          // "lightgray" - Default color for other cases
          return "lightgray_bg";
      }
    }

    if (item?.isAfterHours || item?.futureBookingDisabled) {
      // "lightgray"
      return "lightgray_bg";
    }

    return null; // Default return if no conditions are met
  };

  const getDayIdFromDate = (dateString: string) => {
    const dayName = moment(dateString).format("dddd"); // e.g., "Thursday"
    return recurringWeeks.value.find((week) => week.value === dayName)?.id || 1; // Default to Monday (id: 1)
  };

  const getDuration = (duration: any, getBookingSpacing: number) => {
    // Ensure duration and bookingSpacing are positive numbers
    if (duration < 0 || getBookingSpacing <= 0) {
      throw new Error(
        "Duration must be a positive number and bookingSpacing must be greater than 0",
      );
    }

    // Convert duration to minutes if it's not already
    const durationMinutes =
      typeof duration === "number" ? duration : Math.floor(duration / 60000);

    // Round the duration to the nearest booking spacing
    const roundedMinutes =
      Math.round(durationMinutes / getBookingSpacing) * getBookingSpacing;

    // Convert roundedMinutes to hours and minutes
    const hours = Math.floor(roundedMinutes / 60);
    const minutes = roundedMinutes % 60;

    // Return formatted duration
    return `${hours}:${minutes.toString().padStart(2, "0")}`;
  };

  const mapSelectionToMinutes = async (selectedValue: any) => {
    // Ensure the input is a string
    selectedValue = selectedValue.toString();

    // If the input is a whole number (e.g., "1"), treat it as hours
    if (!selectedValue.includes(".")) {
      return Number(selectedValue) * 60;
    }

    // Split the time string and convert to minutes
    const [hours = "0", minutes = "0"] = selectedValue.split(".").map(Number);
    return hours * 60 + minutes;
  };

  const handleTicketPrint = async (
    type: "PRINT" | "EMAIL",
    bookingId: string,
    printer?: string
  ) => {
    if(type == "PRINT") {
      await pb.collection("bookings").update(bookingId, {requiresPrinting: true, printer: printer});
    }
    if(type == "EMAIL") {
      const booking = await pb.collection("bookings").getOne(bookingId, {expand: "owner,users"});
      const email = booking?.expand?.owner?.email || booking?.expand?.users[0]?.email || booking.visitorEmail;
      await pb.collection("sendQueue").create({
        sendVia: "email",
        recordID: bookingId,
        type: "Booking",
        status: "New",
        user: userId.value,
        address: email,
        action: booking.state
      });
    }
  };

  const reserveSlot = async (
    duration: any,
    date: any,
    time: any,
    activity: string,
    area: any,
    owner: any,
    userId: string,
    pendingBookingId?: any,
    due?: any,
    members?: any[],
    source?: string,
    extSource?: string,
    isAdmin?: boolean,
  ) => {
    // Convert duration to minutes
    const durationTotal = await mapSelectionToMinutes(
      String(duration)?.replace(":", "."),
    );

    // Create ISO start time from date and time
    const startTime = moment(
      `${date} ${time}`,
      "YYYY-MM-DD HH:mm",
    ).toISOString();

    // Define booking data
    const bookingData = {
      startTime,
      duration: durationTotal,
      area,
      activity,
      owner,
      createdBy: userId,
      due,
      state: "Pending",
      users: members,
      source,
      extSource,
    };

    isPendingBooking.value = true;

    if (pendingBookingId) {
      // Update existing booking
      const record = await pb.collection("bookings").getOne(pendingBookingId);
      if (record) {
        Object.assign(record, bookingData, { 
          updatedBy: userId
        }); // Update record with new values
        const response = await pb.collection("bookings").update(record.id, record);

        return response?.id;
      }
    } else {
      // Create new booking
      const response = await pb.collection("bookings").create({
        ...bookingData,
        termsNotAgreedTo: (!bookingData.owner || bookingData.users?.length === 0) && isAdmin ? true : false
      });

      return response?.id;
    }
  };

  const removeExistingPendingBooking = async (bookingId: string, cancelReason?: string, cancelledAtStep?: string) => {
    if (!recurringBookingId.value && !bookingId) return;

    try {
      // Handle recurring bookings
      if (recurringBookingId.value) {
        const { expand: { bookings } = {} } = await pb
          .collection("recurringBookings")
          .getOne(recurringBookingId.value, { expand: "bookings" });

        if (bookings?.length) {
          const deletePromises = bookings
            .filter((booking: any) => booking?.state === "Pending")
            .map((booking: any) =>
              pb.collection("bookings").update(booking.id, {
                state: "Deleted",
                deletedReason: cancelledAtStep ? `${cancelReason} at ${cancelledAtStep} step` : cancelReason,
              }),
            );

          await Promise.all(deletePromises);
        }
      }

      // Handle single booking
      else {
        const { state, docketLine, id } = await pb.collection("bookings").getOne(bookingId);

        if (state === "Pending") {
          await pb.collection("bookings").update(id, { 
            state: "Deleted", 
            value: 0,
            deletedReason: cancelledAtStep ? `${cancelReason} at ${cancelledAtStep} step` : cancelReason,
          });

          if(docketLine) {
            await pb.collection("docketLines").delete(docketLine)
          }
        }
      }
    } catch (error) {
      console.error("Error removing existing pending booking:", error);
    }
  };

  // update booking
  const updateBooking = async (booking: any) => {

    // Format date and time
    const formattedDate = moment(
      `${booking.date} ${booking.time}`,
      "YYYY-MM-DD HH:mm",
    ).toISOString();

    const updateRecord = async (record: any, booking: any) => {
      let bookingTotal = 0;
      let isDocketPaid = false;
      let docketLineID = ''
      let getDocketCheck:any = {}
      let getMemberDiscounts = {expand:{membership:{discountEvents:0,discountProduct:0,discountFurtherBookings:0}}}
      try {
        getMemberDiscounts = await pb.collection("users").getOne(booking.owner, {expand:"membership"})
      } catch(e){
        console.error(e)
      }
      let docketLines:any = []
      try {
        // Get All Docket Lines that have this booking
        docketLines = await pb.collection("docketLines").getFullList({filter: 'booking="' + record.id + '"', sort:"updated", expand:"docket"})
        let docketLineCheck:any = docketLines[docketLines.length - 1]

        // Get all Docket Lines
        if(docketLineCheck && docketLineCheck?.expand?.docket){
          docketId.value = docketLineCheck?.expand?.docket?.id || docketId.value

          try {
            const bookingPaymentLine = docketLines.find((dl: any) =>
              dl.docket === docketId.value &&
              dl.transactionType === "Booking Payment" &&
              !dl.lineText?.includes("Extra Minutes")
            );
            docketLineID = bookingPaymentLine?.id || ''
            getDocketCheck = docketLineCheck?.expand?.docket
            if(getDocketCheck.status == "Paid" || getDocketCheck.status == "Closed"){
              isDocketPaid = true
            }
          } catch(e){console.error(e)}

          let docketLinesCheck = await pb.collection("docketLines").getFullList(
            {filter:'docket="' + docketLineCheck?.expand?.docket?.id  + '" && (transactionType = "Booking Payment" || transactionType = "Product")'}
          )
          if(docketLinesCheck){
            docketLinesCheck.forEach(dLineCheck => {
              bookingTotal += parseFloat(dLineCheck.lineTotal)
            })
          }
        }
      } catch(e){
        console.error(e)
      }
      let due = bookingTotal - parseFloat(record.paid || 0);

      if(booking?.due != null){
        due = parseFloat(booking.due)
      }

      due  = due > 0 ? due : 0
      Object.assign(record, {
        startTime: formattedDate,
        duration: booking.duration,
        area: booking.area,
        users: booking.members,
        activity: booking.activity,
        visitors: booking.guests?.join(","),
        notes: booking.notes,
        due: parseFloat(due.toFixed(2)),      // Update Amount Due
        owner: booking.owner,
        updatedBy: booking.updatedBy,
        createdBy: booking.createdBy,
        discount: booking.discount,
        value: booking.value,
        options: booking.options,
        optionQuantities: getBookingOptionQuantities(booking.options, booking.optionQuantities),
        visitorEmail: booking.visitorEmail,
        visitorPhone: booking.visitorPhone,
        ...(booking.actualStartTime && {
          actualStartTime: booking.actualStartTime,
          actualDuration: booking.actualDuration,
        }),
      });

      // Is Docket Paid or Closed?
      if(isDocketPaid && due == 0){
        delete record.due;
        delete record.value;
      }

      delete record.endTime;

      let allDocketLines = docketLines;
      let allDockets = allDocketLines.map((dl:any) => 'docket="'+ dl.docket +'"').join('||')
      let allLines:any = []
      // Get All Lines
      if(allDockets && allDocketLines.length > 0){
        allLines = await pb.collection("docketLines").getFullList({filter: allDockets, expand:'docket'})
      }

      if (booking?.options && docketId.value && !isDocketPaid) {
        // Fetch existing hired item lines across ALL dockets for this booking (not just the current one)
        let existingHiredLines: any[] = []
        try {
          existingHiredLines = await pb.collection("docketLines").getFullList({
            filter: 'booking="' + record.id + '" && transactionType="Product"',
            expand: 'product'
          })
        } catch(e) {console.error(e)}

        const allproducts = await pb.collection("products").getFullList({filter:'location="' + currentLocation.value + '"', expand:'subCategory,subCategory.TODPricing,category,category.TODPricing'})

        // Delete lines for options that have been removed
        for (const existingLine of existingHiredLines) {
          if (!booking.options.includes(existingLine.product) && existingLine.docket === docketId.value) {
            try {
              await pb.collection("docketLines").update(existingLine.id, { qty: 0, lineTotal: 0, lineDiscountTotal: 0 })
            } catch(e) {
              console.error(e)
            }
          }
        }

        // Create lines for options that have been added (skip ones already on the docket)
        for (const opt of booking.options) {
          const previousQuantity = existingHiredLines.filter((line: any) => line.product === opt && line.docket !== docketId.value)
            .reduce((sum: number, line: any) => sum + Number(line.qty || 0), 0);
          const quantity = Math.max(0, getBookingOptionQuantity(booking.optionQuantities, opt) - previousQuantity);
          const existingLine = existingHiredLines.find((line: any) => line.product === opt && line.docket === docketId.value && line.qty > 0);
          if (existingLine) {
            if (existingLine.qty !== quantity) {
              await pb.collection("docketLines").update(existingLine.id, {
                qty: quantity,
                lineTotal: Number((existingLine.lineTotal / existingLine.qty * quantity).toFixed(2)),
                lineDiscountTotal: Number((existingLine.lineDiscountTotal / existingLine.qty * quantity).toFixed(2)),
              });
            }
            continue;
          }
          if (quantity === 0) continue;

          const pItem: any = allproducts.find((ap: any) => ap.id === opt)
          if (!pItem) continue

          let discount = 0;
          let discountAmount = 0;
          let discountApplied = 'None';

          if(getMemberDiscounts?.expand?.membership?.discountProduct && getMemberDiscounts.expand?.membership.discountProduct > 0) {
            discount = getMemberDiscounts.expand.membership.discountProduct
            discountApplied = "Membership"
          }

          if(getDocketCheck && getDocketCheck?.id){
            try {
              const {discManProducts, discMemProducts, discountProducts, discCodeProducts} = getDocketCheck
              discount = Math.max(discount, discManProducts, discMemProducts, discountProducts, discCodeProducts)
            }catch(e){console.error(e)}
          }

          let productTodAdjustment = 0;
          let productTodSource = "";
          if (booking.time && booking.date) {
            const hour = booking.time.split(":")[0];
            const min = booking.time.split(":")[1];
            const dateTime = moment(booking.date).hour(hour).minute(min);
            const todData = getItemTOD(pItem, dateTime, booking.user, booking.membership);
            productTodAdjustment = todData.todAdjustment;
            productTodSource = todData.todSource;
          }

          const baseAfterTodIncrease = productTodAdjustment > 0
            ? pItem.sellPrice * (1 + productTodAdjustment / 100)
            : pItem.sellPrice;

          const todDiscountValue = productTodAdjustment < 0 ? -productTodAdjustment : 0;
          const finalDiscount = Math.max(discount, todDiscountValue);
          discountAmount = baseAfterTodIncrease * (finalDiscount / 100);
          const itemPrice = baseAfterTodIncrease - discountAmount;

          if (finalDiscount > 0) {
            discountApplied = todDiscountValue > discount ? "TOD" : discountApplied || "Membership";
          }

          const productDocketLineData = {
            docket: docketId.value,
            transactionType: "Product",
            product: opt,
            qty: quantity,
            itemPrice: pItem.sellPrice,
            docketDiscount: discount,
            todAdjustment: productTodAdjustment,
            todSource: productTodSource,
            discountApplied,
            lineDiscountTotal: discountAmount * quantity,
            lineTotal: itemPrice * quantity,
            status: "Available",
            booking: booking.id,
            createdBy: booking.createdBy,
          }

          const newProductDocketLine = await pb.collection("docketLines").create(productDocketLineData)
        }

        allLines = await pb.collection("docketLines").getFullList({filter: allDockets, expand:'docket'})
      }

      // BUG FIX - UPDATE docketLine.....
      if(booking && booking?.duration && booking?.sessionPrice && !isDocketPaid && docketId.value){
        try {
          // Get Activity duration
          let duration = 60;
          try {
            let durationCheck = await pb.collection('activities').getOne(record.activity)
            if(durationCheck && durationCheck?.bookingSpacing){
              duration = Math.floor(durationCheck?.bookingSpacing)
            }
          } catch(e){console.error(e)}

          let quantity = Math.floor(booking?.duration / duration)
          quantity = quantity < 1 ? 1 : quantity

          // Get Past Paid DocketLines and Remove that Duration from this one
          // Exclude the current docket — it may have become Paid mid-flow (hook fired during hired item deletion)
          allLines.forEach((line:any) => {
            if(line.docket !== docketId.value && (line?.expand?.docket?.status == "Paid" || line?.expand?.docket?.status == "Closed") && line?.transactionType == "Booking Payment" && line.lineTotal > 0){
              quantity = quantity - line.qty
            }
          })
          quantity = Math.max(0, quantity)

          const todAdjustmentVal = booking?.todAdjustment || 0;
          let baseLineTotal = parseFloat(booking?.sessionPrice) * quantity;
          if (todAdjustmentVal > 0) {
            baseLineTotal += baseLineTotal * (todAdjustmentVal / 100);
          }

          let updateDocketLine:any = {
            qty: quantity > 0 ? quantity : 1,
            lineTotal: baseLineTotal,
            itemPrice: booking?.sessionPrice,
            docketDiscount: booking.docketDiscount || 0,
            todAdjustment: todAdjustmentVal,
            todSource: booking.todSource || "",
            discountApplied: booking.discountApplied || "None",
            lineDiscountTotal: booking.lineDiscountTotal || 0
          }

          // Get Discounts
          const todDiscountValue = todAdjustmentVal < 0 ? -todAdjustmentVal : 0
          const finalBookingDiscount = Math.max(booking?.docketDiscount || 0, todDiscountValue)

          if(finalBookingDiscount > 0){
            updateDocketLine.lineDiscountTotal = updateDocketLine.lineTotal * (finalBookingDiscount / 100);
            updateDocketLine.lineTotal = updateDocketLine.lineTotal - (updateDocketLine.lineTotal * (finalBookingDiscount / 100))
          }

          updateDocketLine.lineTotal = parseFloat(updateDocketLine.lineTotal).toFixed(2)

          try {
            if(updateDocketLine.lineTotal <= 0){
              updateDocketLine.itemPrice = 0;
            }

            if(docketLineID) {
              await pb.collection("docketLines").update(docketLineID, updateDocketLine)
            } else if(quantity > 0) {
              // Create booking payment line only when there's an actual booking charge
              const newBookingLine = await pb.collection("docketLines").create({
                ...updateDocketLine,
                docket: docketId.value,
                transactionType: "Booking Payment",
                status: "Available",
                booking: record.id,
                createdBy: booking?.createdBy || '',
                updatedBy: booking?.updatedBy || '',
              })
              if (newBookingLine?.id) {
                docketLineID = newBookingLine.id
              }
            }
          } catch (e) {
            console.error("Error updating booking payment docket line:", e)
          }

          // Update or zero out existing Extra Minutes docket lines
          try {
            let allMinuteAmounts = await pb.collection("docketLines").getFullList({filter:'docket="' + docketId.value + '" && lineText~"Extra Minutes Purchased for Booking"'})

            // Find the active extra minutes line (qty > 0) to reuse; prefer it over zeroed lines
            const activeExtraMinLine = allMinuteAmounts?.find((dm: any) => dm.qty > 0)
            const lineToReuse = activeExtraMinLine || allMinuteAmounts?.[0]
            const extraMinItemPrice = parseFloat(booking?.addMinuteAmount) || 0;
            const todDiscountVal = (booking?.todAdjustment || 0) < 0 ? -(booking?.todAdjustment || 0) : 0;
            const extraMinDiscountPercent = Math.max(booking?.docketDiscount || 0, todDiscountVal);
            const extraMinDiscountAmount = extraMinDiscountPercent > 0 ? extraMinItemPrice * (extraMinDiscountPercent / 100) : 0;
            const extraMinLineTotal = extraMinItemPrice - extraMinDiscountAmount;

            if(allMinuteAmounts && allMinuteAmounts.length > 0){
              for(const dm of allMinuteAmounts){
                if(dm.id === lineToReuse?.id && booking?.addMinuteAmount > 0){
                  // Reuse the active line with updated values
                  await pb.collection("docketLines").update(dm.id, {
                    qty: 1,
                    itemPrice: extraMinItemPrice.toFixed(2),
                    lineTotal: extraMinLineTotal.toFixed(2),
                    docketDiscount: booking.docketDiscount || 0,
                    todAdjustment: booking.todAdjustment || 0,
                    todSource: booking.todSource || "",
                    discountApplied: booking.discountApplied || "None",
                    lineDiscountTotal: extraMinDiscountAmount,
                  })
                } else if(dm.qty > 0) {
                  // Zero out any other active extra minutes lines
                  await pb.collection("docketLines").update(dm.id, { qty: 0, lineTotal: 0, lineDiscountTotal: 0 })
                }
              }
            }

            if(!lineToReuse && booking?.addMinuteAmount > 0){
              const extraMinutesDocketLine = await pb.collection("docketLines").create({
                docket: docketId.value,
                transactionType: "Booking Payment",
                qty: 1,
                itemPrice: extraMinItemPrice.toFixed(2),
                lineTotal: extraMinLineTotal.toFixed(2),
                docketDiscount: booking.docketDiscount || 0,
                todAdjustment: booking.todAdjustment || 0,
                todSource: booking.todSource || "",
                discountApplied: booking.discountApplied || "None",
                lineDiscountTotal: extraMinDiscountAmount,
                status: "Available",
                booking: record.id,
                createdBy: booking?.createdBy || '',
                updatedBy: booking?.createdBy || '',
                lineText: booking?.addMinuteAmount ? 'Extra Minutes Purchased for Booking' : ''
              });
              if (extraMinutesDocketLine?.id) {
              }
            }
          } catch (e) {
            console.error("Error handling extra minutes docket lines:", e)
          }

          // Update docket totals after docket line changes
          try {
            // Build docket update object with totals
            const docketUpdate: any = {}

            // Update membership discount on docket if applicable
            const membershipBookingDiscount = getMemberDiscounts?.expand?.membership?.discountFurtherBookings || 0
            if (membershipBookingDiscount > 0) {
              docketUpdate.discMemBookings = membershipBookingDiscount
              // Set the user who the membership discount is coming from
              if (getMemberDiscounts?.id) {
                docketUpdate.user = getMemberDiscounts.id
              }
              // Add Membership to discountSources if not already present
              const currentSources = getDocketCheck?.discountSources || []
              if (!currentSources.includes('Membership')) {
                docketUpdate.discountSources = [...currentSources, 'Membership']
              }
            }

            await pb.collection("dockets").update(docketId.value, docketUpdate)
          } catch (e) {}
        }catch(e) {
          console.error("Error updating docket line for booking update:", e)
        }
      }
      else if(docketLines.length > 0 && booking && booking?.duration && booking?.sessionPrice && isDocketPaid && (docketId.value && due > 0)){
        // GET DOCKET
        let CurrentDocket = await pb.collection("dockets").getOne(docketId.value)

        let CreateNewDocket = {
          user: CurrentDocket.user,
          location: CurrentDocket.location,
          description: CurrentDocket.description,
          status:"Open",
          due:due,
          createdBy: CurrentDocket.createdBy,
          updatedBy: CurrentDocket.updatedBy,
          source:"Booking",
          discCodeEvents:CurrentDocket.discCodeEvents,
          discManEvents:CurrentDocket.discManEvents,
          discMemEvents:CurrentDocket.discMemEvents,
          discountEvents:CurrentDocket.discountEvents,
          discCodeBookings:CurrentDocket.discCodeBookings,
          discManBookings:CurrentDocket.discManBookings,
          discMemBookings:CurrentDocket.discMemBookings,
          discountBookings:CurrentDocket.discountBookings,
          discCodeProducts:CurrentDocket.discCodeProducts,
          discManProducts:CurrentDocket.discManProducts,
          discMemProducts:CurrentDocket.discMemProducts,
          discountProducts:CurrentDocket.discountProducts,
        }

        // Create new Docket
        let newDocket = await pb.collection("dockets").create(CreateNewDocket)
        docketId.value = newDocket.id
        let newDue = due;
        // Add products

        let allproducts = await pb.collection("products").getFullList({filter:'location="' + CurrentDocket.location + '"', expand: "category, category.TODPricing, subCategory, subCategory.TODPricing"})

        if (booking.options && allproducts) {
          // Get All Booking lines and options
          let allOptions:any = []
          let allDocketLines = await pb.collection("docketLines").getFullList({filter: 'booking="'+ record.id +'"'})

          let allDockets = allDocketLines.map(dl => 'docket="'+ dl.docket +'"').join('||')

          // get all Lines
          let allLines = await pb.collection("docketLines").getFullList({filter: allDockets})

          // Get all docketLines for the booking
          booking.options.forEach((opt:any) => {
            const existingQuantity = allLines.filter((line: any) => line.product === opt)
              .reduce((sum: number, line: any) => sum + Number(line.qty || 0), 0);
            const quantity = getBookingOptionQuantity(booking.optionQuantities, opt) - existingQuantity;
            if (quantity > 0) allOptions.push({ id: opt, qty: quantity });
          })

          for (const product of allOptions) {
            let pItem:any = allproducts.filter(ap => ap.id == product?.id)
            if (pItem && pItem[0]) {
              let item = pItem[0]
              let itemPrice = item.sellPrice;
              let docketDiscount = 0;

              // Check the docket for manual discounts if it exists
              if(getDocketCheck && getDocketCheck?.id){
                docketDiscount = getDocketCheck.discountProducts
              }

              // Get todAdjustment for time of booking
              const hour = booking.time.split(":")[0];
              const min = booking.time.split(":")[1];
              const dateTime = moment(booking.date).hour(hour).minute(min);

              const { todAdjustment, todSource } = getItemTOD(item, dateTime, booking.user, booking.membership);

              const todDiscountValue = todAdjustment < 0 ? -todAdjustment : 0;
              const finalDiscount = docketDiscount > todDiscountValue ? docketDiscount : todDiscountValue;
              const discountAmount = (itemPrice || 0) * (finalDiscount / 100);

              const discountApplied = docketDiscount > todDiscountValue ? "Membership" : todDiscountValue > 0 ? "TOD" : "None";

              const productDocketLineData = {
                docket: newDocket.id,
                transactionType: "Product",
                product: product.id,
                qty: product.qty,
                itemPrice: item.sellPrice,
                docketDiscount: docketDiscount,
                todAdjustment: todAdjustment || 0,
                todSource: todSource,
                discountApplied: discountApplied,
                lineDiscountTotal: discountAmount * product.qty,
                lineTotal: (itemPrice - discountAmount) * product.qty,
                status: "Available",
                booking: booking.id,
                createdBy: booking.createdBy,
              };

              newDue = newDue - (itemPrice - discountAmount) * product.qty;

              // Create a new docket line for each product
              const newProductDl = await pb.collection("docketLines").create(productDocketLineData);
            }
          }
        }

        //1.) Create New DocketLine for Booking - only if there's a booking charge
        // Only create booking payment line if newDue > 0 (there's an actual booking charge)
        if (newDue > 0) {
          let newPrice = newDue  // Booking.value - BookingDiscount - paid
          const todDiscountVal = booking.todAdjustment < 0 ? -booking.todAdjustment : 0;
          let discountedAmount = todDiscountVal > booking.docketDiscount ? todDiscountVal : booking.docketDiscount;

          newPrice =  discountedAmount > 0 ? newPrice / (1 - (discountedAmount / 100)) : newDue

          // Calculate the discount amount for THIS line only (not the total booking)
          let newLineDiscountTotal = parseFloat(newPrice.toString()) - parseFloat(newDue.toString());

          const newBookingDocketLine = await pb.collection("docketLines").create({
            docket: newDocket.id,
            transactionType: "Booking Payment",
            qty: 1,
            itemPrice: parseFloat(newPrice.toString()).toFixed(2),
            lineTotal: parseFloat(newDue.toString()).toFixed(2),
            status: "Available",
            booking: record.id,
            docketDiscount: booking.docketDiscount || 0,
            todAdjustment: booking.todAdjustment || 0,
            todSource: booking.todSource || "",
            lineDiscountTotal: newLineDiscountTotal.toFixed(2),
            discountApplied: booking.discountApplied || "None",
            createdBy: CurrentDocket.createdBy,
            updatedBy: CurrentDocket.updatedBy,
            lineText: booking?.addMinuteAmount ? 'Extra Minutes Purchased for Booking' : ''
          });
        }
        
      }

      delete record.emailed;
      delete record.requiresEmail;
      delete record.requiresPrinting;
      delete record.paid;
      await pb.collection("bookings").update(record.id, record);
      await refetchBooking(record.id);
      isPendingBooking.value = false;
    };

    if (!isEmpty(booking?.recurring)) {
      const { id, days, endDate, frequency, interval, isEditAll } = booking.recurring;

      const recurringItems = getSelectedRecurringItem.value(interval);

      const daysString = days
        .map((dayId: number) => recurringItems[dayId]?.value)
        .filter(Boolean)
        .join(", ");

      const endMoment = moment(endDate).startOf("day");
      let currentDate = moment().startOf("day");
      const allDates: any[] = [];

      // Collect valid booking dates
      while (currentDate.isSameOrBefore(endMoment)) {
        days.forEach((dayId: string) => {
          const dayOfWeek = recurringItems[dayId]?.value;
          if (dayOfWeek) {
            const targetDate = currentDate.clone().day(dayOfWeek);

            // Adjust to the next week's day if the target date is in the past
            const nextValidDate = targetDate.isBefore(currentDate)
              ? targetDate.add(1, "week")
              : targetDate;

            // Add the date to the list if it's within the end date range
            if (nextValidDate.isSameOrBefore(endMoment))
              allDates.push(nextValidDate.format("YYYY-MM-DD"));
          }
        });
        currentDate
          .add(frequency, interval === "Weeks" ? "week" : "month")
          .startOf("day");
      }

      if (id) {
        const recurringBookingRecords = await pb
          .collection("recurringBookings")
          .getOne(id, { expand: "bookings" });

        const earliestBooking = (
          recurringBookingRecords?.expand?.bookings || []
        )?.reduce((earliest: any, current: any) => {
          const currentStartTime = moment(current.starttime);
          const earliestStartTime = earliest
            ? moment(earliest.starttime)
            : null;

          // Compare the start times using moment's isBefore method
          return !earliestStartTime ||
            currentStartTime.isBefore(earliestStartTime)
            ? current
            : earliest;
        }, null);

        let bookingId = booking.id || earliestBooking?.id;

        if (recurringBookingRecords?.bookings?.length) {
          // Update booking state if any in pending
          recurringBookingRecords?.expand?.bookings?.map(
            async (booking: any) => {
              if (booking.state === "Pending") {
                await pb.collection("bookings").update(booking?.id, {
                  state: "Confirmed",
                });
              }
            },
          );
          if (isEditAll) {
            await handleRecurringBookingUpdate(
              { ...booking, daysString },
              allDates,
              recurringBookingRecords?.expand?.bookings,
              id,
              endDate,
            );
          } else {
            if (booking?.id) {
              // Update single booking state
              const record = await pb.collection("bookings").getOne(bookingId);
              await updateRecord(record, booking);
            }
          }
        }

        // if booking.id exist then user try to updating the booking so fetch that so payment will further process
        // else recurring booking list exist then fetch the first booking or fetch booking.id
        // recurring booking then the payment flow need to manage for first booking only.
        await refetchBooking(bookingId);
      }
    } else {
      // Handle non-recurring booking
      const record = await pb.collection("bookings").getOne(booking.id);
      await updateRecord(record, booking);
    }
  };

  const refetchBooking = async (bookingId: any = null) => {
    try {
      const id = editBookingData?.value?.id || bookingId;

      let docketLines = await pb.collection("docketLines").getFullList({filter: 'booking="' + id + '"', sort:"created", expand:"booking"})
      let docketLineCheck:any = {}
      let bookingData:any = {}
      if(docketLines && docketLines.length > 0){
        docketLineCheck = docketLines[docketLines.length - 1]
        bookingData = docketLineCheck?.expand?.booking
        bookingData.expand = {docketLine: docketLineCheck}
      } else {
        bookingData = await pb.collection("bookings").getOne(id, {
          expand: "docketLine",
        });
      }

      const status = getBookingStatus(
        bookingData?.actualStartTime,
        bookingData?.actualDuration,
      );


      editBookingData.value = {
        ...bookingData,
        status,
        startDate: bookingData?.startTime,
        docketId: bookingData?.expand?.docketLine?.docket,
        options: bookingData?.options || [],
        selectedOptions: bookingData?.options || [],
      };
    } catch (e) {
      console.error('Error fetching booking:', e);
    }
  };

  const handleRecurringBookingUpdate = async (
    booking: any,
    allDates: string[],
    bookingsList?: any[],
    recurringId?: string,
    endDate?: string,
  ) => {
    const errorDates: string[] = [];
    const bookingIds: string[] = [];
    let allUpdatesSuccessful = true; // Flag to track overall success

    // Convert allDates to a Set for faster lookup
    const allDatesSet = new Set(allDates);

    // Needed to prorate actualDuration (below) for any occurrence that's already been
    // checked in when the series duration changes.
    let bookingSpacing = 0;
    try {
      const activityRecord = await pb.collection('activities').getOne(booking?.activity);
      bookingSpacing = activityRecord?.bookingSpacing || 0;
    } catch (e) {
      console.error('Error fetching activity bookingSpacing for actualDuration proration:', e);
    }

    // Helper function to update or create a booking in a specific area
    const tryBookingInArea = async (
      area: string,
      date: string,
      bookingData: any,
      isUpdate: boolean = false,
    ) => {
      const discountPercentage = bookingData?.discount || 0;
      // Calculate the discount amount
      const discountAmount = (booking?.value * discountPercentage) / 100;
      // Apply the discount and ensure the due amount is non-negative
      const discountedValue = booking?.value - discountAmount;
      const dues = discountedValue - (bookingData?.paid || 0);
      const finalDue = dues > 0 ? dues : 0;

      // If this occurrence has already been checked in (has its own actualStartTime/
      // actualDuration) and the series duration is changing, prorate its actualDuration
      // by the same number of slots the overall duration is changing by - same math as
      // the single-booking edit path in bookingStepper.vue.
      let actualDuration = bookingData?.actualDuration;
      if (
        bookingSpacing > 0 &&
        bookingData?.actualStartTime &&
        bookingData?.actualDuration > 0 &&
        bookingData?.duration !== booking.duration
      ) {
        const slots = booking.duration / bookingSpacing;
        const slotsTaken = bookingData.duration / bookingSpacing;
        actualDuration = bookingData.actualDuration + (slots - slotsTaken) * bookingSpacing;
      }

      const updatedBooking = {
        ...bookingData,
        area: area,
        startTime: moment(
          `${date} ${booking.time}`,
          "YYYY-MM-DD HH:mm",
        ).toISOString(),
        activity: booking?.activity,
        due: finalDue,
        value: booking.value,
        duration: booking.duration,
        options: booking.options,
        optionQuantities: getBookingOptionQuantities(booking.options, booking.optionQuantities),
        ...(bookingData?.actualStartTime && { actualDuration }),
      };
      try {
        if (isUpdate) {
          await pb
            .collection("bookings").update(updatedBooking.id, updatedBooking);
        } else {
          const createdBooking = await pb
            .collection("bookings").create(updatedBooking);
          return createdBooking;
        }
        return true;
      } catch (e) {
        console.error(
          `Error ${
            isUpdate ? "updating" : "creating"
          } booking for ${date} in area ${area}:`,
          e,
        );
        return false;
      }
    };

    // Step 1: Iterate over existing bookings and update them if they match the dates in `allDates`
    for (const existingBooking of bookingsList || []) {
      const existingBookingDate = moment(existingBooking.startTime).format(
        "YYYY-MM-DD",
      );

      if (allDatesSet.has(existingBookingDate)) {
        // Update booking if the date exists in allDates
        let areaUpdated = false;

        // Try booking in different areas in descending order
        for (let i = alternativeAreaList.value?.length - 1; i >= 0; i--) {
          const area = alternativeAreaList.value[i]?.id;
          const success = await tryBookingInArea(
            area,
            existingBookingDate,
            {
              ...existingBooking,
              id: existingBooking.id, // Ensure the ID is included for updates
            },
            true,
          );

          if (success) {
            bookingIds.push(existingBooking.id);
            allDatesSet.delete(existingBookingDate); // Remove from set as it has been handled
            areaUpdated = true;
            break;
          }
        }

        if (!areaUpdated) {
          errorDates.push(existingBookingDate);
          allUpdatesSuccessful = false; // Set flag to false on error
        }
      } else {
        // If booking's date is not in allDates, try to use a new date from allDatesSet to update this booking
        if (allDatesSet.size > 0) {
          const newDate = allDatesSet.values().next().value as any; // Get the first remaining date
          let areaUpdated = false;

          // Try to update with the new date in different areas
          for (let i = alternativeAreaList.value?.length - 1; i >= 0; i--) {
            const area = alternativeAreaList.value[i]?.id;
            booking.area = area;
            const success = await tryBookingInArea(
              area,
              newDate,
              {
                ...existingBooking,
                startTime: moment(
                  `${newDate} ${booking.time}`,
                  "YYYY-MM-DD HH:mm",
                ).toISOString(),
                id: existingBooking.id, // Ensure the ID is included for updates
              },
              true,
            );

            if (success) {
              bookingIds.push(existingBooking.id);
              allDatesSet.delete(newDate); // Remove the used date from set
              areaUpdated = true;
              break;
            }
          }

          if (!areaUpdated) {
            errorDates.push(newDate);
            allUpdatesSuccessful = false; // Set flag to false on error
          }
        } else {
          // No dates left to update with, so this booking is no longer needed
          try {
            console.log(
              `Booking for ${existingBookingDate} is no longer required. Deleting it.`,
            );
            await pb.collection("bookings").update(existingBooking.id, {state: "Deleted"});
          } catch (e) {
            console.error(
              `Error deleting booking for ${existingBookingDate}:`,
              e,
            );
            errorDates.push(existingBookingDate);
            allUpdatesSuccessful = false; // Set flag to false on error
          }
        }
      }
    }

    // Step 2: Create new bookings for remaining dates in `allDatesSet`
    for (const newDate of allDatesSet) {
      let areaBooked = false;

      // Try to book in different areas in descending order
      for (let i = alternativeAreaList.value.length - 1; i >= 0; i--) {
        const area = alternativeAreaList.value[i]?.id;
        const createdBooking = await tryBookingInArea(area, newDate, {
          startTime: moment(
            `${newDate} ${booking.time}`,
            "YYYY-MM-DD HH:mm",
          ).toISOString(),
          duration: booking.duration,
          area: area,
          users: booking.members,
          activity: booking.activity,
          visitors: booking.guests?.join(","),
          notes: booking.notes,
          due: booking.due,
          owner: booking.owner,
          state: "Confirmed",
          discount: booking.discount,
          value: booking.value,
        });

        if (createdBooking) {
          bookingIds.push(createdBooking.id);
          areaBooked = true;
          break;
        }
      }

      if (!areaBooked) {
        console.error(`Failed to book ${newDate} in all available areas.`);
        errorDates.push(newDate);
        allUpdatesSuccessful = false; // Set flag to false on error
      }
    }

    // Step 3: Update or create the recurring bookings record only if all updates and creations were successful
    if (recurringId && allUpdatesSuccessful && bookingIds.length > 0) {
      try {
        const data = {
          bookings: bookingIds,
          days: booking.daysString,
          endDate: endDate,
          frequency: booking.frequency,
          interval: booking.interval,
        };
        await pb.collection("recurringBookings").update(recurringId, data);
      } catch (e) {
        console.error("Error updating recurring booking record:", e);
      }
    } else if (recurringId && !allUpdatesSuccessful) {
      console.error(
        "Not updating recurring record due to errors in booking operations.",
      );
    }

    // Return any dates that had errors
    if (errorDates.length) {
      isBookingWarningDialogVisible.value = true;
      errorDateList.value = Array.from(new Set(errorDates)); // Convert Set back to array
    }
    console.error("Error dates:", errorDates);
  };

  const handleRecurringBooking = async (booking: any) => {
    try {
      // Format date and time
      const formattedDate = moment(
        `${booking.date} ${booking.time}`,
        "YYYY-MM-DD HH:mm",
      ).toISOString();

      const updateRecord = async (record: any, booking: any) => {
        Object.assign(record, {
          startTime: formattedDate,
          duration: booking.duration,
          area: booking.area,
          users: booking.members,
          activity: booking.activity,
          visitors: booking.guests?.join(","),
          notes: booking.notes,
          due: booking.due,
          owner: booking.owner,
          updatedBy: booking.updatedBy,
          state: "Confirmed",
          discount: booking.discount,
          value: booking.value,
        });
        delete record.endTime;
        delete record.requiresEmail;
        delete record.requiresPrinting;
        if (record?.docketLine === docketLineId.value && docketId.value) {
          await pb.collection("docketLines").update(docketLineId.value, {
            qty: booking.slot,
            lineTotal: booking.slot * booking.sessionPrice,
          });
        }
        await pb.collection("bookings").update(record.id, record);
      };

      const handleRecurringBookingCreation = async (
        booking: any,
        allDates: string[],
      ) => {
        let bookingIds: string[] = [];

        // Create individual bookings for each date in the recurring series
        for (const bookingDate of allDates) {
          const bookingParams = {
            startTime: moment(
              `${bookingDate} ${booking.time}`,
              "YYYY-MM-DD HH:mm",
            ).toISOString(),
            duration: booking.duration,
            area: booking.area,
            users: booking.members,
            activity: booking.activity,
            visitors: booking.guests?.join(","),
            notes: booking.notes,
            due: booking.due,
            owner: booking.owner,
            state: "Pending",
            discount: booking.discount,
            value: booking.value,
          };
          const createdBooking = await pb.collection("bookings").create(bookingParams);
          bookingIds.push(createdBooking.id);
        }

        // Prepare data for recurring booking configuration
        const data = {
          bookings: [...bookingIds],
          days: booking?.daysString,
          endDate: booking?.recurring?.endDate,
          frequency: booking?.recurring?.frequency,
          interval: booking?.recurring?.interval,
        };

        const newRecurringBooking = await pb
          .collection("recurringBookings")
          .create(data);
        // Update the booking with the new recurring booking ID
        booking.recurring.id = newRecurringBooking.id;
        recurringBookingId.value = newRecurringBooking.id;
      };

      if (!isEmpty(booking?.recurring)) {
        const { id, days, endDate, frequency, interval, isEditAll } =
          booking.recurring;
        const recurringItems = getSelectedRecurringItem.value(interval);
        const daysString = days
          .map((dayId: number) => recurringItems[dayId]?.value)
          .filter(Boolean)
          .join(", ");

        const endMoment = moment(endDate).startOf("day");
        let currentDate = moment().startOf("day");
        const allDates: any[] = [];

        // Collect valid booking dates
        while (currentDate.isSameOrBefore(endMoment)) {
          days.forEach((dayId: string) => {
            const dayOfWeek = recurringItems[dayId]?.value;
            if (dayOfWeek) {
              const targetDate = currentDate.clone().day(dayOfWeek);

              // Adjust to the next week's day if the target date is in the past
              const nextValidDate = targetDate.isBefore(currentDate)
                ? targetDate.add(1, "week")
                : targetDate;

              // Add the date to the list if it's within the end date range
              if (nextValidDate.isSameOrBefore(endMoment)) {
                allDates.push(nextValidDate.format("YYYY-MM-DD"));
              }
            }
          });
          currentDate
            .add(frequency, interval === "Weeks" ? "week" : "month")
            .startOf("day");
        }

        if (id) {
          const recurringBookingRecords = await pb
            .collection("recurringBookings")
            .getOne(id, { expand: "bookings" });

          if (recurringBookingRecords?.expand?.bookings?.length) {
            if (isEditAll) {
              // Update recurring record
              await handleRecurringBookingUpdate(
                { ...booking, daysString },
                allDates,
                recurringBookingRecords?.expand?.bookings,
                id,
                endDate,
              );
              isPendingBooking.value = false;
            } else {
              // Update single record
              const record = await pb.collection("bookings").getOne(booking.id);
              await updateRecord(record, booking);
            }
          }
        } else {
          // If no recurring booking exists, create a new one
          await handleRecurringBookingCreation(
            { ...booking, daysString },
            allDates,
          );
          isPendingBooking.value = true;
        }
      }
    } catch (e: any) {
      throw e;
    }
  };

  return {
    paymentOptions,
    recurringWeeks,
    recurringEndDateConfig,
    getAreaSingular,
    alternativeAreaList,
    getAvailableHoursForDay,
    getColor,
    reserveSlot,
    getDuration,
    removeExistingPendingBooking,
    mapSelectionToMinutes,
    updateBooking,
    refetchBooking,
    handleRecurringBooking,
    getDayIdFromDate,
    handleTicketPrint,
  };
};
